import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Chip, IconButton, Menu, MenuItem, makeStyles } from '@material-ui/core'
import { Box } from '@mui/material'
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  ContentCopy,
  MoreVert as MoreVertIcon
} from '@material-ui/icons'
import { toast } from 'utils/notify'
import Avatar from 'components/Avatar'
import ResourceNotFound from 'components/ResourceNotFound'
import BasicTable from 'components/BasicTable'
import getTableOptions from 'utils/getTableOptions'
import { renderCol } from 'utils/renderColumns'
import { convertTimestampToLocalString } from 'utils/times'
import { SubjectStatus, Roles, creationType } from 'constants/index'
import { getSpeakers } from 'store/speaker'
import speakerSlice from 'store/speaker'
import ConfirmModal from 'components/ConfirmationModal'
import InviteSpeakerModal from './InviteSpeakerModal'
import SendCustomEmailModal from './SendCustomEmailModal'
import { cloudFunctionsInstance } from 'config/axios'
import { deleteSpeakerById, checkSpeakerExistsInTalks } from 'store/speaker'
import { capitalize, caseInsensitiveSort } from 'utils'
import { chipStyleForOrganization } from 'views/admin/organizations/utils'

// Imported speakers whose status stays PENDING_DATA_REVIEW even after they submit
// (see SpeakerProfile.jsx) - submittedAt is what distinguishes "still needs review" from
// "reviewed, awaiting admin approval"
const isAwaitingReview = (speaker) =>
  speaker?.status === SubjectStatus.PENDING_DATA_REVIEW && !speaker?.submittedAt

// "Invite Sent On" should reflect the latest of the invite/reminder emails sent to the
// speaker, not just the original invitedAt
const getLatestInviteEmailSentAt = (speaker) =>
  Math.max(
    speaker?.invitedAt || 0,
    speaker?.remindedAt || 0,
    speaker?.profileReviewRemindedAt || 0
  ) || null

const SpeakerListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [currentSpeaker, setCurrentSpeaker] = useState(null)

  // New state variables for email functionality
  const [modalOpen, setModalOpen] = useState(false)
  const [bulkModalOpen, setBulkModalOpen] = useState(false)
  const [selectedSpeaker, setSelectedSpeaker] = useState(null)
  const [sendingEmail, setSendingEmail] = useState(false)
  const [isBulkOperation, setIsBulkOperation] = useState(false)
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [speakerToEdit, setSpeakerToEdit] = useState(null)

  const [deletedSpeakerId, setDeletedSpeakerId] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Row selection state
  const [selectedRows, setSelectedRows] = useState([])
  const [customEmailModalOpen, setCustomEmailModalOpen] = useState(false)
  const [fixedSpeakerForEmail, setFixedSpeakerForEmail] = useState(null)

  const { user } = useSelector((state) => state.auth)
  const { speakers, statusFilter, currentPage, pageSize, sortOrder, searchText, inProgress } =
    useSelector((state) => state.speaker)
  const { conference } = useSelector((state) => state.conference)

  const confTimezone = conference?.timezone
  const conf24HrFormat = conference?.is24hrFormat

  const setStatusFilter = (status) => {
    dispatch(speakerSlice.actions.setStatusFilter(status))
  }

  useEffect(() => {
    const unsub = getSpeakers(user?.conferenceId, (data) => {
      dispatch(speakerSlice.actions.setSpeakers(caseInsensitiveSort(data, 'fullName')))
    })
    return () => unsub && unsub()
  }, [dispatch, user?.conferenceId])

  // Statistics for speaker statuses
  const speakerStats = useMemo(() => {
    const stats = {
      [SubjectStatus.INVITE_NOT_SENT]: 0,
      [SubjectStatus.AWAITING_RESPONSE]: 0,
      [SubjectStatus.PENDING_DATA_REVIEW]: 0,
      [SubjectStatus.APPROVED]: 0
    }

    speakers?.forEach((speaker) => {
      const status = speaker?.status || SubjectStatus.INVITE_NOT_SENT
      if (Object.prototype.hasOwnProperty.call(stats, status)) {
        stats[status]++
      } else {
        stats[SubjectStatus.INVITE_NOT_SENT]++
      }
    })

    return stats
  }, [speakers])

  // Filter speakers based on status
  const filteredSpeakers = useMemo(() => {
    if (statusFilter === 'all') {
      return speakers || []
    }
    return (
      speakers?.filter((speaker) => {
        const status = speaker?.status || SubjectStatus.INVITE_NOT_SENT
        return status === statusFilter
      }) || []
    )
  }, [speakers, statusFilter])

  const clearFilters = () => {
    setStatusFilter('all')
    setSelectedRows([])
  }

  // Handle row selection change
  const handleRowSelectionChange = (currentRowsSelected, allRowsSelected) => {
    const selected = allRowsSelected.map((row) => row.dataIndex)
    setSelectedRows(selected)
  }

  // Get selected speakers for custom email modal
  const selectedSpeakers = selectedRows.map((index) => filteredSpeakers[index])

  // Imported speakers in the current view who still haven't reviewed/submitted their profile
  const speakersAwaitingReview = useMemo(
    () => filteredSpeakers?.filter(isAwaitingReview) || [],
    [filteredSpeakers]
  )

  const handleCloseDeleteModal = () => setDeletedSpeakerId(null)

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true)
      const isSpeakerExistInTalk = await dispatch(checkSpeakerExistsInTalks(deletedSpeakerId))
      if (isSpeakerExistInTalk) {
        toast.error(
          'Unable to delete this speaker because it is associated with one or more talks. Remove the speaker from all talks before deleting.'
        )
      } else {
        const res = await dispatch(deleteSpeakerById(user?.conferenceId, deletedSpeakerId))
        res && toast.success(`Speaker deleted.`)
      }
    } catch {
      toast.error(`Something went wrong while deleting the speaker. Please try again later.`)
    } finally {
      setIsDeleting(false)
    }
  }

  const tableHead = [
    {
      name: '',
      options: {
        setCellProps: () => ({ style: { width: '45px' } })
      }
    },
    'First Name',
    'Last Name',
    'Email',
    'Roles',
    'Invite Sent On',
    'Data Received On',
    'Approved On',
    {
      name: 'Status',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    },
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => (
          <div style={{ textAlign: 'center' }}>{renderCol(meta.label)}</div>
        )
      }
    }
  ]

  const formatTableData = (speakers) =>
    speakers?.map((speaker) => [
      <Avatar
        key={speaker?.id}
        height={56}
        width={56}
        variant="cicular"
        path="speakers"
        imageProps={{ style: { objectFit: 'cover', objectPosition: '50% 12%' } }}
        imageName={speaker?.imageName}
        conferenceId={user?.role !== Roles.SUPER_ADMIN ? user.conferenceId : ''}
      />,
      speaker?.firstName,
      speaker?.lastName,
      speaker?.email,
      speaker?.roles?.map((role) => capitalize(role)).join(', '),
      getLatestInviteEmailSentAt(speaker)
        ? convertTimestampToLocalString(
            getLatestInviteEmailSentAt(speaker),
            confTimezone,
            conf24HrFormat
          )
        : 'N/A',
      speaker?.submittedAt
        ? convertTimestampToLocalString(speaker?.submittedAt, confTimezone, conf24HrFormat)
        : 'N/A',
      speaker?.approvedAt
        ? convertTimestampToLocalString(speaker?.approvedAt, confTimezone, conf24HrFormat)
        : 'N/A',
      <Chip
        label={speaker?.status || SubjectStatus.INVITE_NOT_SENT}
        style={{
          ...chipStyleForOrganization(speaker?.status || SubjectStatus.INVITE_NOT_SENT).style
        }}
      />,
      <div style={{ textAlign: 'center' }}>
        <IconButton
          size="small"
          onClick={(event) => {
            setMenuAnchorEl(event.currentTarget)
            setCurrentSpeaker(speaker)
          }}>
          <MoreVertIcon />
        </IconButton>{' '}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl) && currentSpeaker?.id === speaker?.id}
          onClose={() => {
            setMenuAnchorEl(null)
            setCurrentSpeaker(null)
          }}>
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null)
              setCurrentSpeaker(null)
              if (
                speaker?.creationType === creationType.INVITATION &&
                (speaker?.status === SubjectStatus.INVITE_NOT_SENT ||
                  speaker?.status === SubjectStatus.AWAITING_RESPONSE)
              ) {
                // Open invitation modal in edit mode
                setSpeakerToEdit(speaker)
                setInviteModalOpen(true)
                return
              }
              history.push(`${location.pathname}/speakers/edit/${speaker.id}`)
            }}>
            <EditIcon fontSize="small" color="action" style={{ marginRight: 10 }} />
            Edit
          </MenuItem>

          {(speaker?.status === SubjectStatus.INVITE_NOT_SENT ||
            speaker?.status === SubjectStatus.AWAITING_RESPONSE ||
            isAwaitingReview(speaker)) && (
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null)
                setCurrentSpeaker(null)
                setSelectedSpeaker(speaker)
                setModalOpen(true)
              }}>
              <EmailIcon fontSize="small" color="action" style={{ marginRight: 10 }} />
              {speaker?.status === SubjectStatus.INVITE_NOT_SENT
                ? 'Send Invitation'
                : 'Send Reminder'}
            </MenuItem>
          )}

          {speaker?.creationType === creationType.INVITATION &&
            speaker?.status !== SubjectStatus.INVITE_NOT_SENT && (
              <MenuItem
                onClick={() => {
                  setMenuAnchorEl(null)
                  setCurrentSpeaker(null)
                  navigator.clipboard.writeText(
                    `${window.location.origin}/public/speakers/profile?token=${speaker?.token}`
                  )
                  toast.info('Invite URL copied to clipboard', {
                    duration: 1000,
                    isClosable: true
                  })
                }}>
                <ContentCopy fontSize="small" color="action" style={{ marginRight: 10 }} />
                Copy Invite URL
              </MenuItem>
            )}

          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null)
              setCurrentSpeaker(null)
              setFixedSpeakerForEmail(speaker)
              setCustomEmailModalOpen(true)
            }}>
            <EmailIcon fontSize="small" color="action" style={{ marginRight: 10 }} />
            Send Custom Email
          </MenuItem>

          <MenuItem
            onClick={async () => {
              setDeletedSpeakerId(speaker?.id)
              setMenuAnchorEl(null)
              setCurrentSpeaker(null)
            }}>
            <DeleteIcon fontSize="small" color="action" style={{ marginRight: 10 }} />
            Delete
          </MenuItem>
        </Menu>
      </div>
    ])

  const options = {
    ...getTableOptions(),
    selectableRows: 'multiple',
    selectableRowsOnClick: false,
    selectableRowsHeader: true,
    selectableRowsHideCheckboxes: false,
    onRowSelectionChange: handleRowSelectionChange,
    rowsSelected: selectedRows,
    customToolbarSelect: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '8px 12px' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EmailIcon />}
          onClick={() => setCustomEmailModalOpen(true)}>
          Send Custom Email
        </Button>
      </Box>
    )
  }

  return (
    <>
      {/* Speaker Stats Filter Bar */}
      <div className={classes.filterStatsBar}>
        <div className={classes.statsChips}>
          <div className={classes.statsSection}>
            <div className={classes.statChipsContainer}>
              <Chip
                size="large"
                label={`All: ${speakers?.length || 0}`}
                className={statusFilter === 'all' ? classes.activeChip : classes.statChip}
                onClick={() => setStatusFilter('all')}
              />
              {Object.entries(SubjectStatus).map(([key, value]) => (
                <Chip
                  size="large"
                  key={key}
                  label={`${value}: ${speakerStats[value] || 0}`}
                  className={statusFilter === value ? classes.activeChip : classes.statChip}
                  onClick={() => setStatusFilter(value)}
                />
              ))}
            </div>
          </div>
        </div>

        {statusFilter !== 'all' && (
          <Button
            variant="outlined"
            color="primary"
            size="medium"
            className={classes.clearButton}
            onClick={clearFilters}
            startIcon={<CloseIcon />}>
            Clear Filters
          </Button>
        )}
      </div>

      {speakers && speakers.length ? (
        filteredSpeakers?.length > 0 ? (
          <>
            {/* Conditional button for bulk operations */}
            {(statusFilter === SubjectStatus.INVITE_NOT_SENT ||
              statusFilter === SubjectStatus.AWAITING_RESPONSE ||
              statusFilter === SubjectStatus.PENDING_DATA_REVIEW) && (
              <div
                style={{
                  marginBottom: 16,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginRight: 8
                }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<EmailIcon />}
                  onClick={() => setBulkModalOpen(true)}
                  disabled={
                    sendingEmail ||
                    (statusFilter === SubjectStatus.PENDING_DATA_REVIEW &&
                      !speakersAwaitingReview.length)
                  }>
                  {statusFilter === SubjectStatus.INVITE_NOT_SENT
                    ? 'Send Invitations to All'
                    : statusFilter === SubjectStatus.AWAITING_RESPONSE
                    ? 'Send Reminders to All Waiting for Data'
                    : 'Send Reminders to All Pending Review'}
                </Button>
              </div>
            )}
            <BasicTable
              data={formatTableData(filteredSpeakers || [])}
              columns={tableHead}
              options={{
                ...options,
                onChangePage: (page) => dispatch(speakerSlice.actions.setCurrentPage(page)),
                onChangeRowsPerPage: (size) => dispatch(speakerSlice.actions.setPageSize(size)),
                page: currentPage,
                rowsPerPage: pageSize,
                sortOrder,
                onColumnSortChange: (changedColumn, direction) =>
                  dispatch(speakerSlice.actions.setSortOrder({ name: changedColumn, direction })),
                searchText,
                searchOpen: !!searchText,
                onSearchChange: (text) => dispatch(speakerSlice.actions.setSearchText(text || '')),
                onSearchClose: () => dispatch(speakerSlice.actions.setSearchText(''))
              }}
            />
          </>
        ) : (
          <ResourceNotFound message={`No speakers with status "${statusFilter}" available`} />
        )
      ) : (
        !inProgress && <ResourceNotFound message="No speaker available" />
      )}

      {/* Single Speaker Email Confirmation Modal */}
      <ConfirmModal
        open={modalOpen}
        closeModal={() => {
          setModalOpen(false)
          setSelectedSpeaker(null)
        }}
        dialogTitle={
          selectedSpeaker?.status === SubjectStatus.INVITE_NOT_SENT
            ? 'Send Invitation Email'
            : 'Send Reminder Email'
        }
        dialogText={
          selectedSpeaker?.status === SubjectStatus.INVITE_NOT_SENT
            ? `Are you sure you want to send an invitation email to ${selectedSpeaker?.fullName}?`
            : `Are you sure you want to send a reminder email to ${selectedSpeaker?.fullName}?`
        }
        inProgress={sendingEmail && !isBulkOperation}
        handleConfirm={async () => {
          setSendingEmail(true)
          try {
            await cloudFunctionsInstance.post(
              isAwaitingReview(selectedSpeaker)
                ? '/remindSpeakersToReviewProfile'
                : '/inviteOrRemindSpeakersViaEmail',
              {
                speakerIds: [selectedSpeaker.id],
                conferenceId: user?.conferenceId
              }
            )
            toast.success(
              selectedSpeaker?.status === SubjectStatus.INVITE_NOT_SENT
                ? 'Speaker invitation email sent.'
                : 'Speaker reminder email sent.'
            )
          } catch (err) {
            const errorMessage =
              err?.response?.data?.msg || 'Failed to send email, Please try again later'
            toast.error(errorMessage)
          } finally {
            setSendingEmail(false)
            setModalOpen(false)
            setSelectedSpeaker(null)
          }
        }}
      />

      {/* Bulk Email Confirmation Modal */}
      <ConfirmModal
        open={bulkModalOpen}
        closeModal={() => {
          setBulkModalOpen(false)
        }}
        dialogTitle={
          statusFilter === SubjectStatus.INVITE_NOT_SENT
            ? 'Send Bulk Invitation Emails'
            : 'Send Bulk Reminder Emails'
        }
        dialogText={
          statusFilter === SubjectStatus.INVITE_NOT_SENT
            ? `Are you sure you want to send invitation emails to all ${filteredSpeakers?.length} speakers?`
            : statusFilter === SubjectStatus.AWAITING_RESPONSE
            ? `Are you sure you want to send reminder emails to all ${filteredSpeakers?.length} speakers waiting for data?`
            : `Are you sure you want to send reminder emails to the ${speakersAwaitingReview?.length} speaker(s) who haven't reviewed/submitted their imported profile yet?`
        }
        inProgress={sendingEmail && isBulkOperation}
        handleConfirm={async () => {
          setSendingEmail(true)
          setIsBulkOperation(true)
          const isReviewReminder = statusFilter === SubjectStatus.PENDING_DATA_REVIEW
          try {
            await cloudFunctionsInstance.post(
              isReviewReminder
                ? '/remindSpeakersToReviewProfile'
                : '/inviteOrRemindSpeakersViaEmail',
              {
                speakerIds: (isReviewReminder ? speakersAwaitingReview : filteredSpeakers)
                  ?.filter((speaker) => speaker?.id)
                  .map((speaker) => speaker?.id),
                conferenceId: user?.conferenceId
              }
            )
            toast.success(
              statusFilter === SubjectStatus.INVITE_NOT_SENT
                ? 'Invitation emails sent successfully to all speakers.'
                : isReviewReminder
                ? 'Reminder emails sent successfully to speakers pending review.'
                : 'Reminder emails sent successfully to all waiting speakers.'
            )
          } catch (err) {
            const errorMessage =
              err?.response?.data?.msg || 'Failed to send emails, Please try again later'
            toast.error(errorMessage)
          } finally {
            setSendingEmail(false)
            setBulkModalOpen(false)
            setIsBulkOperation(false)
          }
        }}
      />

      <ConfirmModal
        dialogTitle="Delete Speaker"
        dialogText="Do you really want to delete this speaker?"
        open={!!deletedSpeakerId}
        closeModal={handleCloseDeleteModal}
        handleConfirm={handleConfirmDelete}
        inProgress={isDeleting}
      />

      {/* Speaker Invitation Edit Modal */}
      <InviteSpeakerModal
        open={inviteModalOpen}
        onClose={() => {
          setInviteModalOpen(false)
          setSpeakerToEdit(null)
        }}
        editMode={true}
        speakerData={speakerToEdit}
      />

      {/* Send Custom Email Modal */}
      <SendCustomEmailModal
        open={customEmailModalOpen}
        onClose={() => {
          setCustomEmailModalOpen(false)
          setSelectedRows([])
          setFixedSpeakerForEmail(null)
        }}
        selectedRecipients={fixedSpeakerForEmail ? [fixedSpeakerForEmail] : selectedSpeakers}
        availableRecipients={speakers || []}
      />
    </>
  )
}

export default SpeakerListing

const useStyles = makeStyles((theme) => ({
  filterStatsBar: {
    marginTop: '10px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderRadius: '8px',
    border: '1px solid rgba(0, 0, 0, 0.04)',
    margin: '0px 8px 16px 8px'
  },
  statsChips: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  statsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  statChipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  statChip: {
    fontSize: '15px',
    padding: '4px 8px',
    letterSpacing: '0.5px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)'
    }
  },
  activeChip: {
    fontSize: '15px',
    letterSpacing: '0.5px',
    padding: '4px 8px',
    backgroundColor: `${theme?.palette?.primary?.main || '#1976d2'} !important`,
    color: 'white',
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: theme?.palette?.primary?.dark || '#115293'
    }
  },
  clearButton: {
    whiteSpace: 'nowrap'
  }
}))
